import { Fragment } from 'react';
import { Box, Text } from '@nimbus-ds/components';
import { DataList } from '@nimbus-ds/patterns';
import { v4 as uuidv4 } from 'uuid';
import { ProductTransferDetailsResponseDto } from '@tiendanube/common';
import { HiddenVariantTag } from 'domains/Catalog/Categories/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  TransferDetailStatus,
  TransferProductCell,
} from 'domains/Catalog/Transfers/pages/components';
import { extractTransferProductDetails } from 'domains/Catalog/Transfers/utils';

import './TransferList.scss';

type ProductVariant =
  ProductTransferDetailsResponseDto['productVariants'][number];

interface TransferListProps {
  productVariants: ProductVariant[];
}

function TransferList({ productVariants }: Readonly<TransferListProps>) {
  const t = useTranslationCatalog();
  const products = extractTransferProductDetails(productVariants);

  if (productVariants.length === 0) return null;

  return (
    <Box data-style="stratus--transfer-list">
      <DataList>
        <div className="stratus--margins-expand">
          {products.map((product) => {
            const variants = productVariants.filter(
              (v) => v.productId === product.productId,
            );
            return (
              <DataList.Row
                id={product.productId}
                key={product.productId}
                topDivider
              >
                <TransferProductCell product={product} />
                <Box
                  display="grid"
                  gridTemplateColumns="30fr 30fr 30fr 10fr"
                  gridGap="1-5"
                  marginTop={variants.length > 1 ? '5' : 'none'}
                >
                  {variants.map((variant, index) => (
                    <Fragment key={uuidv4()}>
                      <div style={{ gridColumn: '1/5' }}>
                        <Box display="flex" flexDirection="row" gap="2">
                          <Text color="neutral-textHigh" lineClamp={1}>
                            {variant.variantName}
                          </Text>
                          <HiddenVariantTag visible={variant.variantVisible} />
                        </Box>
                      </div>
                      <Box />
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('transfers.detail.table.headers.requested')}
                      </Text>
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('transfers.detail.table.headers.done')}
                      </Text>
                      <Box />
                      <Box />
                      <Text color="neutral-textHigh">{variant.quantity}</Text>
                      <Text color="neutral-textHigh">
                        {variant.quantityDone}
                      </Text>

                      <TransferDetailStatus
                        status={variant.status}
                        isPartial={variant.quantityDone < variant.quantity}
                      />

                      {index !== variants.length - 1 && (
                        <div
                          style={{
                            gridColumn: '1/-1',
                            margin: '8px -16px 0 -16px',
                            height: '1px',
                            backgroundColor:
                              'var(--nimbus-colors-neutral-surfaceHighlight)',
                          }}
                        />
                      )}
                    </Fragment>
                  ))}
                </Box>
              </DataList.Row>
            );
          })}
        </div>
      </DataList>
    </Box>
  );
}

export default TransferList;
